import { createContext, useContext, useEffect, useRef, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getRemoteConfig,
  fetchAndActivate,
  getAll,
} from "firebase/remote-config";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updatePassword,
  getIdTokenResult,
  User,
} from "firebase/auth";
import { RootContext } from "./RootContext";
import { setClientLogTransport, logClientError } from "../helpers/clientLogger";
import { ROLE } from "../shared/constants";
import { AdminApi } from "./adminApi";
import { AppSpinner } from "../components/app-spinner/AppSpinner";
import type { AuthContextValue } from "../types/Context";
import type { UserRole } from "../types/Roles";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { markFresh } from "../store/pollScheduler";

const firebaseConfig = JSON.parse(
  process.env.REACT_APP_FIREBASE_CONFIG as string
);
const app = initializeApp(firebaseConfig);

const AuthContext = createContext<AuthContextValue | null>(null);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { showError, showSuccess } = useContext(RootContext)!;
  const [tenant, setTenant] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>(ROLE.UNKNOWN);
  const [userTimezone, setUserTimezone] = useState("");
  const [fetchingUser, setFetchingUser] = useState(true);
  const [fetchingRole, setFetchingRole] = useState(false);
  const [fetchingTenants, setFetchingTenants] = useState(false);
  const [api, setApi] = useState<AdminApi | null>(null);
  const [anonymousApi, setAnonymousApi] = useState<AdminApi | null>(null);

  // Remote Config
  const [remoteConfigValues, setRemoteConfigValues] = useState({});
  const remoteConfig = getRemoteConfig(app);

  const [tenants, setTenants] = useState<any[]>([]);
  const getTenantsInFlight = useRef<Promise<void> | null>(null);
  const [therapists, setTherapists] = useState({});
  const [therapistInvitations, setTherapistInvitations] = useState({});
  const [tenantClients, setTenantClients] = useState<Record<string, any[]>>({});
  const [isTenantSuperAdminInTherapistMode, setTherapistMode] =
    useLocalStorage<boolean>("uplife.therapistMode", false);
  const refreshUserClaimsRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    fetchAndActivate(remoteConfig)
      .then(() => {
        const allParams = getAll(remoteConfig);
        const remoteConfigKeys = [
          "calendar",
          "mood_tracking_reminders_en",
          "mood_tracking_reminders_uk",
          "mood_tracking_reminders_ru",
        ];

        const config = remoteConfigKeys.reduce((acc, key) => {
          if (allParams[key]) {
            const value = allParams[key].asString();
            try {
              acc[key] = JSON.parse(value);
            } catch {
              // silent: non-JSON remote config values fall back to the raw string
              acc[key] = value;
            }
          }
          return acc;
        }, {});
        setRemoteConfigValues(config);
      })
      .catch((err) => {
        showError("Remote Config error: " + err.message);
      });
  }, []);

  useEffect(() => {
    setAnonymousApi(new (AdminApi as any)());
    const auth = getAuth(app);
    onAuthStateChanged(auth, (user) => {
      setUser(user);
      setFetchingUser(false);
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setFetchingRole(false);
      setClientLogTransport(null);
      return;
    }
    const api = new (AdminApi as any)(user, app, "auth");
    api.setUnauthorizedHandler(() => {
      refreshUserClaimsRef.current();
    });
    api.setAccountDeactivatedHandler(() => {
      logOut();
    });
    setApi(api);
    setClientLogTransport((events) => api.sendClientLogs(events));
    // Expose ID token on window so E2E tests can call authenticated APIs
    // without needing to intercept network requests. Dev-only; no-op in prod.
    if (process.env.NODE_ENV === "development") {
      user.getIdToken().then((token: string) => {
        (window as any).__uplifeAuthToken__ = token;
      });
    }
    setFetchingRole(true);
    api
      .getRole()
      .then(({ role, tenant, timezone }: { role: UserRole; tenant: any; timezone: string }) => {
        setRole(role);
        setTenant(tenant);
        setUserTimezone(timezone);
      })
      .catch((error: any) => showError(error))
      .finally(() => setFetchingRole(false));
  }, [user]); // eslint-disable-line

  // Initial tenants fetch on login so the sidebar branding has data. The 30s
  // refresh now lives in TenantsList (usePageData) — it only runs while that
  // page is open. markFresh keeps the first All-Practices visit from reloading.
  useEffect(() => {
    if (!api || role !== ROLE.SUPER_ADMIN) return;
    getTenants().then(() => markFresh("tenants"));
  }, [api, role]); // eslint-disable-line

  async function signInWithEmailPassword(
    email: string,
    password: string,
    tenantId: string
  ) {
    const auth = getAuth(app);
    if (tenant) {
      auth.tenantId = tenant.tenantId;
    } else if (tenantId) {
      auth.tenantId = tenantId;
    }
    const credentials = await signInWithEmailAndPassword(auth, email, password);
    setUser(credentials.user);
  }

  async function signInWithGoogle() {
    try {
      const auth = getAuth(app);
      auth.tenantId = tenant.tenantId;

      const provider = new GoogleAuthProvider();
      provider.addScope("profile");
      provider.addScope("email");
      const credentials = await signInWithPopup(auth, provider);
      const user = credentials.user;
      setUser(user);
      GoogleAuthProvider.credentialFromResult(credentials);
    } catch (error: any) {
      showError(error);
    }
  }

  async function changePassword(newPassword: string) {
    const auth = getAuth(app);
    try {
      await updatePassword(auth.currentUser!, newPassword);
      showSuccess("Password has been changed");
    } catch (error: any) {
      showError(error);
      throw error;
    }
  }

  async function refreshUserClaims() {
    await getIdTokenResult(user!, true)
      .then(() => api!.getRole())
      .then(({ role, tenant, timezone }) => {
        setRole(role);
        setTenant(tenant);
        setUserTimezone(timezone);
      })
      .catch((error: any) => showError(error));
  }

  refreshUserClaimsRef.current = refreshUserClaims;

  function logOut(goHome = true) {
    const auth = getAuth(app);
    return signOut(auth).then(() => {
      setRole(ROLE.UNKNOWN);
      setUser(null);
      setTenant(null);
      setUserTimezone("");
      setTherapistMode(false);
      if (goHome) window.location.href = "#/";
    });
  }

  function patchTenant(tenantId: string, patch: Record<string, unknown>) {
    setTenants((prev: any[]) =>
      prev.map((t) => (t.tenantId === tenantId ? { ...t, ...patch } : t))
    );
  }

  function patchTherapist(tenantId: string, uid: string, patch: Record<string, unknown>) {
    setTherapists((prev: Record<string, any[]>) => ({
      ...prev,
      [tenantId]: (prev[tenantId] ?? []).map((t: any) => {
        if (t.uid !== uid) return t;
        const customClaims = patch.customClaims
          ? { ...(t.customClaims ?? {}), ...(patch.customClaims as Record<string, unknown>) }
          : t.customClaims;
        return { ...t, ...patch, customClaims };
      }),
    }));
  }

  function patchTherapistInvitation(tenantId: string, email: string, patch: Record<string, unknown>) {
    setTherapistInvitations((prev: Record<string, any[]>) => ({
      ...prev,
      [tenantId]: (prev[tenantId] ?? []).map((inv: any) => {
        if (inv.email !== email) return inv;
        const customClaims = patch.customClaims
          ? { ...(inv.customClaims ?? {}), ...(patch.customClaims as Record<string, unknown>) }
          : inv.customClaims;
        return { ...inv, ...patch, customClaims };
      }),
    }));
  }

  function removeTherapistInvitation(tenantId: string, email: string) {
    setTherapistInvitations((prev: Record<string, any[]>) => ({
      ...prev,
      [tenantId]: (prev[tenantId] ?? []).filter((inv: any) => inv.email !== email),
    }));
  }

  function addTherapistInvitation(tenantId: string, inv: Record<string, unknown>) {
    setTherapistInvitations((prev: Record<string, any[]>) => ({
      ...prev,
      [tenantId]: [...(prev[tenantId] ?? []), inv],
    }));
  }

  async function getTenants(silent?: boolean) {
    if (!api) return;
    // Dedupe concurrent loads: AuthContext's initial fetch (for the sidebar)
    // and TenantsList's usePageData both fire on a reload; without this they
    // hit the backend twice at once. Sharing one in-flight promise also covers
    // the rare poll/refresh overlap.
    if (getTenantsInFlight.current) return getTenantsInFlight.current;
    const run = (async () => {
      try {
        setFetchingTenants(true);
        const data = await api.getTenants({ skipLog: !!silent });
        setTenants(data);
      } catch (error: any) {
        if (!silent) showError(error);
        else logClientError(error, { surface: "background-poll:tenants", severity: "warn" });
      } finally {
        setFetchingTenants(false);
        getTenantsInFlight.current = null;
      }
    })();
    getTenantsInFlight.current = run;
    return run;
  }

  async function createTenant({ tenantName }: { tenantName: string }) {
    try {
      if (!api) return;
      await api.createTenant({ name: tenantName });
      await getTenants();
      showSuccess("Tenant successfully created");
    } catch (error: any) {
      showError(error);
      throw error;
    }
  }

  async function getTherapists(tenantId: string, silent?: boolean) {
    try {
      if (!api || !tenantId) return;
      const data = await api.getTherapists(tenantId, { skipLog: !!silent });
      setTenants((prev: any[]) => {
        const idx = prev.findIndex((t) => t.tenantId === tenantId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            displayName: data.displayName,
          };
          return next;
        }
        return [...prev, { tenantId, displayName: data.displayName }];
      });
      setTherapists({
        ...therapists,
        [tenantId]: data.therapists,
      });
    } catch (error: any) {
      if (!silent) showError(error);
      else logClientError(error, { surface: "background-poll:therapists", severity: "warn" });
    }
  }

  async function getTherapistInvitations(tenantId: string, silent?: boolean) {
    try {
      if (!api || !tenantId) return;
      const data = await api.getInvitations(tenantId, { skipLog: !!silent });
      setTenants((prev: any[]) => {
        const idx = prev.findIndex((t) => t.tenantId === tenantId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            displayName: data.displayName,
          };
          return next;
        }
        return [...prev, { tenantId, displayName: data.displayName }];
      });
      setTherapistInvitations({
        ...therapistInvitations,
        [tenantId]: data.invitations,
      });
    } catch (error: any) {
      if (!silent) showError(error);
      else logClientError(error, { surface: "background-poll:therapists", severity: "warn" });
    }
  }

  async function logActivity(activity: string) {
    if (!api || !user) return;
    return await api.__logUserActivity(user.uid, role, activity);
  }

  const providedData: AuthContextValue = {
    user,
    role,
    userTimezone,
    tenant: tenant?.displayName,
    ownTenantId: tenant?.tenantId,
    ownTenant: tenant,
    setTenant,
    signInWithEmailPassword,
    signInWithGoogle,
    logOut,
    changePassword,
    refreshUserClaims,
    api,
    anonymousApi,
    getTenants,
    patchTenant,
    createTenant,
    getTherapists,
    getTherapistInvitations,
    patchTherapist,
    patchTherapistInvitation,
    removeTherapistInvitation,
    addTherapistInvitation,
    tenants,
    fetchingTenants,
    therapists,
    therapistInvitations,
    tenantClients,
    setTenantClients,
    remoteConfigValues,
    logActivity,
    isTenantSuperAdminInTherapistMode,
    setTherapistMode,
  };
  return (
    <AuthContext.Provider value={providedData}>
      {fetchingUser || (user !== null && api === null) || fetchingRole ? (
        <AppSpinner />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

export const useApi = (): AdminApi => {
  const { api } = useAuth();
  if (!api) throw new Error("useApi called before api was initialized");
  return api;
};
